import { ReactNode, useContext, useEffect, useMemo } from "react";

import { useBooking } from "../../providers/BookingProvider.tsx";
import { ConfigurationContext } from "../../providers/ConfigurationContext.tsx";
import { useVehicleSelection } from "../../providers/VehicleSelectionContext.tsx";
import { Woocommerce } from "../../types/Order.ts";
import Button from "../elements/Button.tsx";
import Header from "../elements/Header.tsx";
import { NewOrderScreen } from "./NewOrderScreen.tsx";

interface OrderErrorProps {
  header: string;
  backgroundClass?: string;
  children: ReactNode;
}

const OrderError = ({
  header,
  backgroundClass = "bg-red-200",
  children,
}: OrderErrorProps) => {
  return (
    <div className={`${backgroundClass} rounded-xl p-4 my-4`}>
      <Header size="2xl" hideUnderline>
        {header}
      </Header>
      {children}
    </div>
  );
};

export const EditOrderScreen = () => {
  const { orderId } = useContext(ConfigurationContext);
  const { order, isLoadingOrder, orderError } = useBooking();
  const {
    handleMakeModelChange,
    handleVariantChange,
    handleGvmUpgradeChange,
    allGvmUpgrades,
    handleGvmWeightChange,
    handleOptionChange,
    handleOptionDetailChange,
    handleNotesChange,
  } = useVehicleSelection();

  const bundleProduct = useMemo(() => {
    if (!order) {
      return null;
    }
    return order?.items.find(
      (item) => item.item_data?.bundle_data,
    ) as Woocommerce.BundleOrderItem | null;
  }, [order]);

  useEffect(() => {
    if (!bundleProduct) {
      return;
    }

    const vehicles = bundleProduct.item_data.vehicles!;

    handleMakeModelChange(vehicles[1].term_id);
    vehicles.forEach((vehicle, index) => {
      if (index > 1) {
        handleVariantChange(index - 2, vehicle.term_id);
      }
    });
  }, [bundleProduct, handleMakeModelChange, handleVariantChange]);

  useEffect(() => {
    if (!bundleProduct) {
      return;
    }

    const weight = bundleProduct.item_data.weight?.slug.match(/(\d+)/)?.[0];
    if (weight) {
      handleGvmWeightChange(weight ?? null);
    }

    const gvmUpgradeId = Number(bundleProduct.item_data._product_id);
    handleGvmUpgradeChange(gvmUpgradeId);

    const gvmUpgrade = allGvmUpgrades.find(
      (gvmUpgrade) => gvmUpgrade.id === gvmUpgradeId,
    );
    if (gvmUpgrade) {
      const weight =
        gvmUpgrade.extensions.cc_complex_bundle.taxonomies.gvm_upgrade_cat[0].name.match(
          /(\d+)/,
        )?.[0] ?? null;
      handleGvmWeightChange(weight);
    }
  }, [
    bundleProduct,
    handleGvmUpgradeChange,
    allGvmUpgrades,
    handleGvmWeightChange,
  ]);

  useEffect(() => {
    if (!bundleProduct) {
      return;
    }

    if (bundleProduct.item_data.notes) {
      handleNotesChange(bundleProduct.item_data.notes);
    }
  }, [bundleProduct, handleNotesChange]);

  useEffect(() => {
    if (!bundleProduct) {
      return;
    }

    for (const [
      stepIndex,
      step,
    ] of bundleProduct.item_data.bundle_data[0].steps.entries()) {
      for (const [entryIndex, entry] of step.entries.entries()) {
        if (entry.selected_product_option) {
          handleOptionChange(
            stepIndex,
            entryIndex,
            entry.selected_product_option.option_index,
          );

          if (entry.selected_product_option.quantity !== undefined) {
            const optionKey = `${stepIndex}-${entryIndex}-${entry.selected_product_option.option_index}`;
            handleOptionDetailChange(optionKey, {
              quantity: entry.selected_product_option.quantity,
            });
          }
        }
        if (entry.selected_product_options) {
          const selections = entry.selected_product_options.map(
            (option) => option.option_index,
          );
          handleOptionChange(stepIndex, entryIndex, selections);
        }
      }
    }
  }, [bundleProduct, handleOptionChange, handleOptionDetailChange]);

  const viewOrderInPortal = () => {
    window.open(
      import.meta.env.VITE_JMACX_BACKEND_URL + "/wordpress-order/" + orderId,
      "_blank",
    );
  };

  if (isLoadingOrder) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="relative w-32 h-32">
          <div className="absolute w-full h-full border-4 border-gray-200 rounded-full"></div>
          <div className="absolute w-full h-full border-4 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (orderError || !order) {
    let message = orderError?.message || "An unknown error occurred.";
    if (orderError?.code === "woocommerce_rest_invalid_user") {
      message = "You don't have permission to access to this order.";
    }
    return (
      <OrderError header={`Unable to open order #${orderId}`}>
        {message}
      </OrderError>
    );
  }

  if (!bundleProduct) {
    return (
      <OrderError header={`Order #${orderId} can't be updated here`}>
        <div className="mb-4">
          This order doesn&apos;t contain a GVM upgrade kit.
        </div>
        <Button onClick={() => viewOrderInPortal()}>
          View order in portal
        </Button>
      </OrderError>
    );
  }

  if (!order.extensions?.jmacx_is_editable) {
    return (
      <OrderError header={`Order #${orderId} can't currently be updated`}>
        <div className="mb-4">
          The order may be already completed or the GVM upgrade kit may be
          locked. Check the order in the portal for more information.
        </div>
        <Button onClick={() => viewOrderInPortal()}>
          View order in portal
        </Button>
      </OrderError>
    );
  }

  const vehicleName = bundleProduct.item_data
    .vehicles!.map((item) => item.name)
    .join(" ");

  const formatter = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  });

  // Woocommerce returns the price in cents
  const totalPrice =
    Number(order.totals.total_price) /
    Math.pow(10, order.totals.currency_minor_unit);

  return (
    <>
      <div className="mb-[24px] mt-[16px]">
        <div className="bg-primary p-4 text-white rounded-xl">
          <Header size="3xl" hideUnderline>
            Order #{orderId}
          </Header>
          <ul>
            <li>
              Customer: {order.billing_address.first_name}{" "}
              {order.billing_address.last_name}
              {order.billing_address.company &&
                ` (${order.billing_address.company})`}
            </li>
            <li>Vehicle: {vehicleName}</li>
            <li>
              GVM Upgrade: {bundleProduct.name} (#
              {bundleProduct.item_data._product_id})
            </li>
            <li>
              Order Total:
              {" " + formatter.format(totalPrice)}
            </li>
          </ul>
        </div>
        <NewOrderScreen />
      </div>
    </>
  );
};
